import type {
  ExpressionSpecification,
  LayerSpecification,
  StyleSpecification,
} from 'maplibre-gl'

// The planner's own basemap: OpenMapTiles-schema vector tiles from
// OpenFreeMap (free, no key), drawn in a pale near-monochrome palette in the
// spirit of a modern online VFR map. A VFR chart's job is the aeronautical overlay,
// so the ground stays quiet: faint land cover, grey-blue water, thin roads,
// no POIs. Runways and the national border stay legible because pilots
// navigate by them.
//
// Every colour comes from one token set per theme mode, so the dark map is
// the same style with different tokens rather than a second style to keep
// in step.

export type BasemapMode = 'light' | 'dark'

interface Palette {
  land: string
  wood: string
  grass: string
  residential: string
  building: string
  water: string
  waterway: string
  aeroway: string
  runway: string
  road: string
  roadMajor: string
  roadCasing: string
  rail: string
  border: string
  adminBorder: string
  label: string
  labelMajor: string
  labelHalo: string
  waterLabel: string
}

const PALETTES: Record<BasemapMode, Palette> = {
  light: {
    land: '#f3f3f1',
    wood: '#e8ece5',
    grass: '#eef0ea',
    residential: '#ebebe8',
    building: '#e2e2de',
    water: '#d4dce4',
    waterway: '#c7d2dc',
    aeroway: '#e4e4e8',
    runway: '#b4b4bc',
    road: '#ffffff',
    roadMajor: '#ffffff',
    roadCasing: '#d9d9d6',
    rail: '#d0d0cc',
    border: '#9696a2',
    adminBorder: '#c9c9cf',
    label: '#6e6e76',
    labelMajor: '#3f3f47',
    labelHalo: '#f3f3f1',
    waterLabel: '#7b8ea2',
  },
  dark: {
    land: '#1a1f24',
    wood: '#1d2429',
    grass: '#1c2227',
    residential: '#20262b',
    building: '#262c32',
    water: '#0f1a24',
    waterway: '#15222e',
    aeroway: '#242b32',
    runway: '#4e5760',
    road: '#2a3137',
    roadMajor: '#343c43',
    roadCasing: '#14191d',
    rail: '#2e353b',
    border: '#65707c',
    adminBorder: '#333b43',
    label: '#8a95a1',
    labelMajor: '#c3cad2',
    labelHalo: '#1a1f24',
    waterLabel: '#56708a',
  },
}

const SOURCE = 'openmaptiles'
/** The first label layer; overlays go beneath it so place names stay readable. */
export const BASEMAP_FIRST_LABEL_LAYER = 'water-name'
export const BASEMAP_FONT = ['Noto Sans Regular']
export const BASEMAP_FONT_BOLD = ['Noto Sans Bold']
const FONT_ITALIC = ['Noto Sans Italic']

const isPolygon: ExpressionSpecification = ['match', ['geometry-type'], ['Polygon', 'MultiPolygon'], true, false]
const isLine: ExpressionSpecification = ['match', ['geometry-type'], ['LineString', 'MultiLineString'], true, false]

function classIn(...classes: string[]): ExpressionSpecification {
  return ['match', ['get', 'class'], classes, true, false]
}

/** Line width by zoom: [zoom, width] stops, interpolated exponentially. */
function width(...stops: [number, number][]): ExpressionSpecification {
  return ['interpolate', ['exponential', 1.4], ['zoom'], ...stops.flat()] as ExpressionSpecification
}

const NAME: ExpressionSpecification = ['coalesce', ['get', 'name:latin'], ['get', 'name']]

function roadLayers(p: Palette): LayerSpecification[] {
  const road = (
    id: string,
    classes: string[],
    minzoom: number,
    color: string,
    stops: [number, number][],
  ): LayerSpecification => ({
    id,
    type: 'line',
    source: SOURCE,
    'source-layer': 'transportation',
    minzoom,
    filter: ['all', isLine, classIn(...classes)],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: { 'line-color': color, 'line-width': width(...stops) },
  })
  // Casings only from z10: below that a two-tone road is noise under
  // airspace boundaries.
  const casing = (id: string, classes: string[], stops: [number, number][]) =>
    road(id, classes, 10, p.roadCasing, stops)
  return [
    road('road-minor', ['minor', 'service', 'tertiary'], 11, p.road, [
      [11, 0.5],
      [16, 6],
    ]),
    casing('road-major-casing', ['primary', 'secondary', 'trunk'], [
      [10, 2.2],
      [16, 12],
    ]),
    road('road-major', ['primary', 'secondary', 'trunk'], 7, p.roadMajor, [
      [7, 0.4],
      [10, 1.2],
      [16, 10],
    ]),
    casing('road-motorway-casing', ['motorway'], [
      [10, 3],
      [16, 14],
    ]),
    road('road-motorway', ['motorway'], 5, p.roadMajor, [
      [5, 0.4],
      [10, 1.8],
      [16, 12],
    ]),
    {
      id: 'railway',
      type: 'line',
      source: SOURCE,
      'source-layer': 'transportation',
      minzoom: 9,
      filter: ['all', isLine, classIn('rail')],
      paint: { 'line-color': p.rail, 'line-width': width([9, 0.5], [16, 2]) },
    },
  ]
}

function labelLayers(p: Palette): LayerSpecification[] {
  const place = (
    id: string,
    classes: string[],
    minzoom: number,
    size: [number, number][],
    major: boolean,
  ): LayerSpecification => ({
    id,
    type: 'symbol',
    source: SOURCE,
    'source-layer': 'place',
    minzoom,
    filter: classIn(...classes),
    layout: {
      'text-field': NAME,
      'text-font': major ? BASEMAP_FONT_BOLD : BASEMAP_FONT,
      'text-size': ['interpolate', ['linear'], ['zoom'], ...size.flat()] as ExpressionSpecification,
      'text-max-width': 8,
    },
    paint: {
      'text-color': major ? p.labelMajor : p.label,
      'text-halo-color': p.labelHalo,
      'text-halo-width': 1.4,
    },
  })
  return [
    {
      id: 'water-name',
      type: 'symbol',
      source: SOURCE,
      'source-layer': 'water_name',
      minzoom: 5,
      filter: ['match', ['geometry-type'], ['Point', 'MultiPoint'], true, false],
      layout: { 'text-field': NAME, 'text-font': FONT_ITALIC, 'text-size': 11, 'text-max-width': 6 },
      paint: { 'text-color': p.waterLabel, 'text-halo-color': p.water, 'text-halo-width': 1 },
    },
    place('place-village', ['village', 'suburb', 'hamlet'], 10, [
      [10, 10],
      [14, 13],
    ], false),
    place('place-town', ['town'], 7, [
      [7, 10],
      [12, 14],
    ], false),
    place('place-city', ['city'], 4, [
      [4, 11],
      [10, 16],
    ], true),
    place('place-country', ['country'], 2, [
      [2, 11],
      [6, 14],
    ], true),
  ]
}

export function basemapStyle(mode: BasemapMode): StyleSpecification {
  const p = PALETTES[mode]
  return {
    version: 8,
    name: `jupi-basemap-${mode}`,
    glyphs: 'https://tiles.openfreemap.org/fonts/{fontstack}/{range}.pbf',
    sources: {
      [SOURCE]: {
        type: 'vector',
        url: 'https://tiles.openfreemap.org/planet',
        attribution:
          '<a href="https://openfreemap.org" target="_blank">OpenFreeMap</a> ' +
          '<a href="https://www.openmaptiles.org/" target="_blank">© OpenMapTiles</a> ' +
          '<a href="https://www.openstreetmap.org/copyright" target="_blank">© OpenStreetMap contributors</a>',
      },
    },
    layers: [
      { id: 'background', type: 'background', paint: { 'background-color': p.land } },
      {
        id: 'landcover-wood',
        type: 'fill',
        source: SOURCE,
        'source-layer': 'landcover',
        filter: ['all', isPolygon, classIn('wood')],
        paint: { 'fill-color': p.wood },
      },
      {
        id: 'landcover-grass',
        type: 'fill',
        source: SOURCE,
        'source-layer': 'landcover',
        minzoom: 8,
        filter: ['all', isPolygon, classIn('grass', 'farmland', 'wetland')],
        paint: { 'fill-color': p.grass },
      },
      {
        id: 'landuse-residential',
        type: 'fill',
        source: SOURCE,
        'source-layer': 'landuse',
        filter: ['all', isPolygon, classIn('residential', 'suburb', 'neighbourhood', 'commercial', 'industrial')],
        paint: { 'fill-color': p.residential },
      },
      {
        id: 'water',
        type: 'fill',
        source: SOURCE,
        'source-layer': 'water',
        filter: ['all', isPolygon, ['!=', ['get', 'brunnel'], 'tunnel']],
        paint: { 'fill-color': p.water },
      },
      {
        id: 'waterway',
        type: 'line',
        source: SOURCE,
        'source-layer': 'waterway',
        minzoom: 8,
        filter: isLine,
        paint: { 'line-color': p.waterway, 'line-width': width([8, 0.5], [14, 2]) },
      },
      {
        id: 'building',
        type: 'fill',
        source: SOURCE,
        'source-layer': 'building',
        minzoom: 13,
        paint: { 'fill-color': p.building },
      },
      {
        id: 'aeroway-area',
        type: 'fill',
        source: SOURCE,
        'source-layer': 'aeroway',
        minzoom: 10,
        filter: ['all', isPolygon, classIn('aerodrome', 'apron')],
        paint: { 'fill-color': p.aeroway },
      },
      ...roadLayers(p),
      {
        id: 'aeroway-taxiway',
        type: 'line',
        source: SOURCE,
        'source-layer': 'aeroway',
        minzoom: 12,
        filter: ['all', isLine, classIn('taxiway')],
        paint: { 'line-color': p.runway, 'line-width': width([12, 0.5], [16, 6]) },
      },
      {
        id: 'aeroway-runway',
        type: 'line',
        source: SOURCE,
        'source-layer': 'aeroway',
        // OpenMapTiles carries aeroways from z10; below that the airspace
        // overlay's aerodrome symbols do the job.
        minzoom: 10,
        filter: ['all', isLine, classIn('runway')],
        paint: { 'line-color': p.runway, 'line-width': width([10, 1.5], [12, 5], [16, 40]) },
      },
      {
        id: 'boundary-admin',
        type: 'line',
        source: SOURCE,
        'source-layer': 'boundary',
        minzoom: 7,
        filter: [
          'all',
          ['>=', ['get', 'admin_level'], 3],
          ['<=', ['get', 'admin_level'], 4],
          ['!=', ['get', 'maritime'], 1],
        ],
        paint: { 'line-color': p.adminBorder, 'line-width': 0.8, 'line-dasharray': [3, 2] },
      },
      {
        id: 'boundary-country',
        type: 'line',
        source: SOURCE,
        'source-layer': 'boundary',
        filter: ['all', ['==', ['get', 'admin_level'], 2], ['!=', ['get', 'maritime'], 1]],
        paint: { 'line-color': p.border, 'line-width': width([3, 0.8], [10, 1.6]), 'line-dasharray': [4, 2] },
      },
      ...labelLayers(p),
    ],
  }
}
